/**
 * Central configuration for sections available in different landing page builders
 *
 * Usage:
 * 1. Define sections with their metadata (label, image, category)
 * 2. Configure which sections are available in each builder type
 * 3. Import and use in AddSection components
 */

import { ISection } from './types';
import { Option } from './container/BlockSelectModal';
import ProgramListImg from './assets/ProgramsList.png';
import PaidProgramListImg from './assets/PaidProgramsList.png';
import TextSectionImg from './assets/TextSection.png';
import TextSection1Img from './assets/TextSection1.png';
import TextSection2Img from './assets/TextSection2.png';
import BioImg from './assets/Bio.png';
import TestimonialsImg from './assets/Testimonials.png';
import ReviewsImg from './assets/Reviews.png';
import LogosImg from './assets/Logos.png';
import HighlightedProgramImg from './assets/HighlightedProgram.png';
import FeaturedBenefitImg from './assets/FeaturedBenefit.png';
import FeaturesImg from './assets/Features.png';
import EmailCaptureImg from './assets/EmailCapture.png';
import offerCarousel from './assets/offercarousel.png';
import offerDisplay from './assets/offerdisplay.png';
import faq from './assets/faq.png';
import featuredoffer from './assets/featuredoffer.png';

// Section metadata type
type SectionMetadata = {
  value: ISection['type'] | string; // Allow string for custom sections
  label: string;
  image?: string;
  category: 'promotion' | 'content';
  /** Conditional display - only show if this function returns true */
  condition?: (context: BuilderContext) => boolean;
};

// Context passed to section conditions
type BuilderContext = {
  isOTPOnly?: boolean;
  hasOfferId?: boolean;
  hasPublishedPrograms?: boolean;
  hasSubscriptionOffer?: boolean;
  hasOtpOffers?: boolean;
  [key: string]: any; // Allow custom context properties
};

// Builder types
export type BuilderType =
  | 'channel-landing-page' // Main legacy business model channel landing page
  | 'offer-channel-landing-page' // Main bundle business model channel landing page
  | 'otp-landing-page' // One-Time purchase landing page
  | 'offer-landing-page'; // Offer-specific landing page

/**
 * All available sections with their metadata
 * This is the master list - add new sections here
 */
const ALL_SECTIONS: Record<string, SectionMetadata> = {
  // Content sections
  ProgramsList: {
    value: 'ProgramsList',
    label: 'Subscription Programs Carousel',
    image: ProgramListImg,
    category: 'content',
    condition: (ctx) => !ctx.isOTPOnly, // Only show if NOT OTP-only
  },
  PaidProgramsList: {
    value: 'PaidProgramsList',
    label: 'One-Time Purchases Carousel',
    image: PaidProgramListImg,
    category: 'content',
  },
  TextSection: {
    value: 'TextSection',
    label: 'Headline + text',
    image: TextSectionImg,
    category: 'content',
  },
  TextSection1: {
    value: 'TextSection1',
    label: 'Headline + text + media',
    image: TextSection1Img,
    category: 'content',
  },
  TextSection2: {
    value: 'TextSection2',
    label: 'Headline + text + two images',
    image: TextSection2Img,
    category: 'content',
  },
  Workouts: {
    value: 'Workouts',
    label: 'Workouts',
    category: 'content',
  },
  FAQs: {
    value: 'FAQs',
    label: 'FAQ',
    category: 'content',
    image: faq,
  },
  OfferDetails: {
    value: 'OfferDetails',
    label: 'Offer display',
    category: 'content',
    image: offerDisplay,
    condition: (ctx) => ctx.hasOfferId === true, // Only show if offer ID is available
  },

  // Promotion sections
  EmailCapture: {
    value: 'EmailCapture',
    label: 'Email Pop-Up',
    image: EmailCaptureImg,
    category: 'promotion',
  },
  Bio: {
    value: 'Bio',
    label: 'Bio',
    image: BioImg,
    category: 'promotion',
  },
  Testimonials: {
    value: 'Testimonials',
    label: 'Testimonials',
    image: TestimonialsImg,
    category: 'promotion',
  },
  Reviews: {
    value: 'Reviews',
    label: 'Reviews',
    image: ReviewsImg,
    category: 'promotion',
  },
  Logos: {
    value: 'Logos',
    label: 'Publishers',
    image: LogosImg,
    category: 'promotion',
  },
  ProgramsHighlight: {
    value: 'ProgramsHighlight',
    label: 'Featured program',
    image: HighlightedProgramImg,
    category: 'promotion',
  },
  FeaturedOffer: {
    value: 'FeaturedOffer',
    label: 'Featured offer',
    category: 'promotion',
    image: featuredoffer,
    // Only show if there is an offer to feature
    condition: (ctx) => ctx.hasOtpOffers === true,
  },
  OffersCarousel: {
    value: 'OffersCarousel',
    label: 'Offers carousel',
    image: offerCarousel,
    category: 'promotion',
    condition: (ctx) => ctx.hasOtpOffers === true, // Only show if a selectable OTP bundle exists
  },
  SubscriptionCarousel: {
    value: 'SubscriptionCarousel',
    label: 'Subscription content carousel',
    image: offerCarousel,
    category: 'content',
    condition: (ctx) => ctx.hasSubscriptionOffer === true, // Only show if an active subscription bundle exists
  },
  FeaturedBenefit: {
    value: 'FeaturedBenefit',
    label: 'Featured benefit',
    image: FeaturedBenefitImg,
    category: 'promotion',
  },
  FeaturesNew: {
    value: 'FeaturesNew',
    label: 'App features',
    image: FeaturesImg,
    category: 'promotion',
  },
  OTPFeatures: {
    value: 'OTPFeatures',
    label: 'App features',
    image: FeaturesImg,
    category: 'promotion',
  },
};

/**
 * Configuration for each builder type
 *
 * To customize which sections appear in a builder:
 * 1. Add/remove section keys from the array
 * 2. Sections will be filtered by category automatically
 * 3. Conditional sections will be filtered by their condition function
 */
const BUILDER_CONFIGS: Record<BuilderType, string[]> = {
  // Main channel landing page - most features
  'channel-landing-page': [
    // Content
    'ProgramsList',
    'PaidProgramsList',
    'TextSection',
    'TextSection1',
    'TextSection2',
    'FAQs',
    // Promotion
    'EmailCapture',
    'Bio',
    'Testimonials',
    'Reviews',
    'Logos',
    'ProgramsHighlight',
    'FeaturedOffer',
    'OffersCarousel',
    'FeaturedBenefit',
    'FeaturesNew',
  ],
  'offer-channel-landing-page': [
    // Content
    'SubscriptionCarousel',
    'TextSection',
    'TextSection1',
    'TextSection2',
    'FAQs',
    // Promotion
    'EmailCapture',
    'Bio',
    'Testimonials',
    'Reviews',
    'Logos',
    'ProgramsHighlight',
    'FeaturedOffer',
    'OffersCarousel',
    'FeaturedBenefit',
    'FeaturesNew',
  ],
  // OTP landing page - focused on single purchases
  'otp-landing-page': [
    // Content
    'TextSection1',
    'TextSection2',
    'Workouts',
    'FAQs',
    // Promotion
    'EmailCapture',
    'Bio',
    'Testimonials',
    'Reviews',
    'OTPFeatures',
  ],

  // Offer landing page - similar to OTP but for offers
  'offer-landing-page': [
    // Content
    'TextSection1',
    'TextSection2',
    'FAQs',
    'OfferDetails',
    // Promotion
    'EmailCapture',
    'Bio',
    'Testimonials',
    'Reviews',
  ],
};

/**
 * Get sections for a specific builder type
 *
 * @param builderType - Type of builder
 * @param context - Context for conditional sections
 * @returns Array of sections grouped by category (compatible with BlockSelectModal)
 */
export function getSectionsForBuilder(
  builderType: BuilderType,
  context: BuilderContext = {},
): Array<{ title: string; value: Option[] }> {
  const sectionKeys = BUILDER_CONFIGS[builderType] || [];

  // Get sections with their metadata and filter by conditions
  const sectionsWithMeta = sectionKeys
    .map((key) => ({ key, section: ALL_SECTIONS[key] }))
    .filter(({ section }) => {
      if (!section) return false;
      if (!section.condition) return true;
      return section.condition(context);
    });

  // Group by category and convert to Option format
  const promotionSections = sectionsWithMeta
    .filter(({ section }) => section.category === 'promotion')
    .map(
      ({ section }): Option => ({
        value: section.value as ISection['type'],
        label: section.label,
        image: section.image,
      }),
    );

  const contentSections = sectionsWithMeta
    .filter(({ section }) => section.category === 'content')
    .map(
      ({ section }): Option => ({
        value: section.value as ISection['type'],
        label: section.label,
        image: section.image,
      }),
    );

  return [
    { title: 'Promotion', value: promotionSections },
    { title: 'Content', value: contentSections },
  ];
}
